import { useState } from "react";
import { Wand2, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SectionLabel } from "@/components/common";
import Reveal from "@/components/Reveal";
import AiText from "@/components/AiText";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AgentRecommender() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState("");
  const [needs, setNeeds] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!business.trim() || !needs.trim()) { toast.error("Tell us about your business and what you need."); return; }
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/ai/recommend", { business, needs });
      setResult(data.recommendation);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate a recommendation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 border-t border-white/8 bg-[#07111F]/40">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal>
          <SectionLabel>AI Agent Recommender</SectionLabel>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            Not sure which agent you need? Ask our AI.
          </h2>
          <p className="mt-3 text-base text-slate-400 max-w-2xl">
            Describe your business and your biggest time-drains — we'll suggest the custom AI agents to build for you.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-start">
            <form onSubmit={submit} className="vora-card rounded-2xl p-6 space-y-4" data-testid="recommender-form">
              <div>
                <label className="text-sm text-slate-300">Your business / role</label>
                <Input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="e.g. A 12-person logistics company" className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="recommender-business" />
              </div>
              <div>
                <label className="text-sm text-slate-300">What do you need help with?</label>
                <Textarea value={needs} onChange={(e) => setNeeds(e.target.value)} placeholder="e.g. Too much time answering customer emails and chasing shipment updates." className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF] min-h-[110px]" data-testid="recommender-needs" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold gap-2" data-testid="recommender-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {loading ? "Thinking…" : "Recommend my AI agents"}
              </Button>
            </form>

            <div className="vora-card rounded-2xl p-6 min-h-[260px]" data-testid="recommender-result">
              {result ? (
                <>
                  <AiText text={result} />
                  <Button onClick={() => navigate("/contact")} variant="outline" className="mt-5 bg-transparent border-[#4F8CFF]/30 text-white hover:bg-[#4F8CFF]/10 rounded-full gap-2 group" data-testid="recommender-cta">
                    Talk to us about building these
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                  <Wand2 className="h-8 w-8 text-[#4F8CFF]/50" />
                  <p className="mt-3 text-sm text-slate-500 max-w-xs">Your tailored AI agent recommendations will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
